/**
 * IndexedDB Cache Module for Murphy Door Configurator
 *
 * Provides persistent caching for the Perfect Product Dictionary (PPD) using
 * IndexedDB. The PPD is an ~8MB data structure containing Maps and Sets that
 * exceed localStorage's 4MB limit. IndexedDB's structured clone algorithm
 * preserves Maps and Sets natively without serialization. Note: Object.freeze()
 * state is NOT preserved on round-trip — reads return a fresh mutable copy.
 *
 * Design:
 * - Lazy initialization: DB connection deferred until first use
 * - Graceful degradation: Returns null/false silently when IndexedDB is
 *   unavailable (private browsing, storage quota exceeded, etc.)
 * - Upsert semantics via IDBObjectStore.put()
 * - No external dependencies
 *
 * All external communication flows through md-config-core-conductor.js conductor
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  const DB_NAME = 'murphy_door_ppd_cache';
  const DB_VERSION = 1;
  const STORE_NAME = 'ppd_entries';

  class IdbCache {
    constructor() {
      this._db = null;
      this._dbPromise = null;
      // iOS Safari 17+ silently closes IDB connections on tab suspension without
      // firing onclose. Guard both pagehide (full navigation/close) and
      // visibilitychange→hidden (tab switch / home button) — iOS can suspend
      // and drop the IDB connection on either event without firing db.onclose.
      const _closeDb = () => {
        // Unconditional: prevents a stale onsuccess from re-arming _db
        // after iOS suspends the tab mid-open (race condition guard).
        this._dbPromise = null;
        if (this._db) {
          // Mark BEFORE close(). close() sets the connection's "close pending"
          // flag synchronously; any transaction() during that window throws
          // InvalidStateError. _withStore captures `db` in a local across its
          // await, so it can still hold this reference after we drop ours —
          // and a closing connection STILL reports its objectStoreNames, so
          // the contains() guard cannot detect it. This mark is how it knows.
          this._db.__mdClosing = true;
          this._db.close();
          this._db = null;
        }
      };
      window.addEventListener('pagehide', _closeDb, { passive: true });
      // visibilitychange fires on document per spec; window delivery is unreliable
      // on iOS Safari 15 and earlier during OS-level background transitions.
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') _closeDb();
        },
        { passive: true }
      );
    }

    // ========================================================================
    // PRIVATE: Lazy DB initialization
    // ========================================================================

    /**
     * Opens (or returns the existing) IndexedDB connection.
     * Uses a single cached promise so concurrent calls share the same open.
     * @returns {Promise<IDBDatabase|null>}
     */
    _getDb() {
      if (this._db) {
        return Promise.resolve(this._db);
      }

      if (this._dbPromise) {
        return this._dbPromise;
      }

      this._dbPromise = new Promise((resolve) => {
        try {
          if (typeof indexedDB === 'undefined' || !indexedDB) {
            isDev() &&
              console.warn(
                '[IdbCache] IndexedDB not available in this environment'
              );
            resolve(null);
            return;
          }

          const request = indexedDB.open(DB_NAME, DB_VERSION);

          request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              db.createObjectStore(STORE_NAME, { keyPath: 'productId' });
              isDev() &&
                console.log('[IdbCache] Created object store:', STORE_NAME);
            }
          };

          request.onsuccess = (event) => {
            // If _closeDb() fired while IDB was opening (tab suspended mid-open),
            // _dbPromise was nulled before onsuccess ran. Close the fresh connection
            // immediately so we don't leave an orphaned open handle on a suspended page.
            if (this._dbPromise === null) {
              event.target.result.close();
              resolve(null);
              return;
            }
            this._db = event.target.result;

            this._db.onclose = () => {
              isDev() &&
                console.log(
                  '[IdbCache] Database connection closed unexpectedly'
                );
              // Mark the connection object itself — a caller suspended at its
              // await may still hold this exact reference (see _closeDb).
              if (this._db) this._db.__mdClosing = true;
              this._db = null;
              this._dbPromise = null;
            };

            // stopPropagation stops IDB error events from bubbling within the IDB chain
            // (request → transaction → database). On Android Chrome, IDB events do not
            // propagate through the DOM tree, so this does NOT affect window.onerror —
            // the actual window.onerror suppression comes from resolving null rather than rejecting.
            // Works on WebKit/Safari (unlike preventDefault which is a no-op when cancelable:false).
            this._db.onerror = (event) => {
              event.stopPropagation();
              isDev() &&
                console.warn('[IdbCache] Database error:', event.target.error);
            };

            // Gracefully close when a newer DB version is opened in another tab.
            // Note: Safari often skips this event for same-version multi-tab opens;
            // the objectStoreNames.contains() guard below is the primary Safari defense.
            this._db.onversionchange = () => {
              isDev() &&
                console.log(
                  '[IdbCache] DB version change detected, closing connection'
                );
              this._db.__mdClosing = true;
              this._db.close();
              this._db = null;
              this._dbPromise = null;
            };

            isDev() && console.log('[IdbCache] Database opened successfully');
            resolve(this._db);
          };

          request.onerror = (event) => {
            event.stopPropagation();
            isDev() &&
              console.warn(
                '[IdbCache] Failed to open database:',
                event.target.error
              );
            resolve(null);
          };

          request.onblocked = (event) => {
            event.stopPropagation();
            isDev() &&
              console.warn(
                '[IdbCache] Database open blocked by another connection'
              );
            resolve(null);
          };
        } catch (error) {
          isDev() &&
            console.warn('[IdbCache] Exception opening database:', error);
          // Do not null _dbPromise here — preserve the settled promise so
          // concurrent callers (e.g. iOS Safari Private Browsing where
          // indexedDB.open() throws synchronously) don't retry endlessly.
          resolve(null);
        }
      });

      return this._dbPromise;
    }

    /**
     * Wraps a transaction-based operation in a promise with error handling.
     * @param {string} mode - 'readonly' or 'readwrite'
     * @param {function(IDBObjectStore): IDBRequest} operation - Receives the store, returns the request
     * @returns {Promise<*>} Resolves with the request result, or null on error
     */
    async _withStore(mode, operation) {
      try {
        const db = await this._getDb();
        if (!db) {
          return null;
        }

        // The connection can be closed while we are suspended at the await
        // above — pagehide, visibilitychange->hidden and versionchange all fire
        // on the task queue and run _closeDb()/onclose/onversionchange before
        // this line resumes. `db` is a local captured BEFORE that, so it
        // outlives this._db being nulled, and db.transaction() on a
        // close-pending connection throws InvalidStateError ("The database
        // connection is closing."). objectStoreNames stays populated on a
        // closing connection, so the contains() guard below cannot catch it.
        // Bail to the same graceful null the catch would have produced, but
        // without throwing — this was the top first-party JS error in Clarity.
        if (db.__mdClosing || (this._db !== null && this._db !== db)) {
          isDev() &&
            console.warn(
              '[IdbCache] Connection closed or replaced mid-operation — skipping'
            );
          return null;
        }

        // Guard against stale DB connections where the object store is missing.
        // On Safari, onversionchange may not fire for same-version multi-tab opens —
        // this guard catches the stale-connection scenario reliably cross-browser.
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          isDev() &&
            console.warn(
              '[IdbCache] Object store missing — resetting stale DB reference'
            );
          this._db = null;
          this._dbPromise = null;
          return null;
        }

        return new Promise((resolve) => {
          try {
            const transaction = db.transaction(STORE_NAME, mode);
            const store = transaction.objectStore(STORE_NAME);
            const request = operation(store);

            request.onsuccess = () => resolve(request.result);

            request.onerror = (event) => {
              event.stopPropagation();
              isDev() &&
                console.warn(
                  '[IdbCache] Store operation failed:',
                  event.target.error
                );
              resolve(null);
            };

            transaction.onerror = (event) => {
              // stopPropagation (not preventDefault) — avoids spec side effect of
              // suppressing transaction abort while still stopping IDB-chain escalation.
              event.stopPropagation();
              isDev() &&
                console.warn(
                  '[IdbCache] Transaction failed:',
                  event.target.error
                );
              resolve(null);
            };

            transaction.onabort = (event) => {
              // QuotaExceededError on low-storage Android fires onabort, not onerror.
              // Without this handler the Promise would hang indefinitely.
              event.stopPropagation();
              isDev() &&
                console.warn(
                  '[IdbCache] Transaction aborted:',
                  event.target.error
                );
              resolve(null);
            };
          } catch (error) {
            // Reset the stale DB reference so the next call reopens the connection
            // rather than reusing the broken one (which would error again immediately).
            if (this._db === db) {
              this._db = null;
              this._dbPromise = null;
            }
            isDev() &&
              console.warn('[IdbCache] Transaction setup failed:', error);
            resolve(null);
          }
        });
      } catch (error) {
        isDev() && console.warn('[IdbCache] _withStore failed:', error);
        return null;
      }
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Retrieves a cached PPD entry by product ID.
     * @param {string} productId
     * @returns {Promise<{ppd: *, productVersion: string, globalVersion: string, hash: string, timestamp: number}|null>}
     */
    async get(productId) {
      const result = await this._withStore('readonly', (store) =>
        store.get(productId)
      );

      if (result) {
        isDev() && console.log('[IdbCache] Cache hit for product:', productId);
      } else {
        isDev() && console.log('[IdbCache] Cache miss for product:', productId);
      }

      return result || null;
    }

    /**
     * Stores a PPD entry with metadata. Uses put() for upsert semantics.
     * The object is stored via structured clone, preserving Maps, Sets, and
     * frozen objects without JSON serialization.
     * @param {string} productId
     * @param {Object} data - { ppd, productVersion, globalVersion, hash, timestamp }
     * @returns {Promise<boolean>} True if stored successfully, false otherwise
     */
    async set(productId, data) {
      // 🚨 THIS IS AN ALLOW-LIST, AND ANYTHING MISSING FROM IT IS SILENTLY
      // DROPPED. Rebuilding the entry field-by-field (rather than storing
      // `data`) means a field added to cachePPD's cacheData but not added here
      // never reaches disk, reads back as `undefined`, and takes whatever
      // decision depends on it with it -- with no error anywhere.
      //
      // That is not hypothetical: `contractVersion` was added to cachePPD and
      // omitted here, so every read compared undefined (-> 0) against
      // PPD_CONTRACT_VERSION (1), rejected the cache, and deleted it. The
      // result was a PPD rebuild on EVERY visit -- the cache silently stopped
      // existing while looking perfectly healthy in code. Caught on staging by
      // reading the persisted row back, not by any unit test.
      //
      // Keep this list in step with cacheData in md-config-core-initializer.js
      // `cachePPD()`. tests/assets/ppd-cache-contract.test.js asserts the two
      // agree.
      const entry = {
        productId: productId,
        ppd: data.ppd,
        productVersion: data.productVersion,
        globalVersion: data.globalVersion,
        contractVersion: data.contractVersion,
        hash: data.hash,
        timestamp: data.timestamp,
      };

      const result = await this._withStore('readwrite', (store) =>
        store.put(entry)
      );

      const success = result !== null;
      isDev() &&
        console.log(
          '[IdbCache]',
          success
            ? 'Stored PPD for product:'
            : 'Failed to store PPD for product:',
          productId
        );

      return success;
    }

    /**
     * Removes a cached PPD entry by product ID.
     * @param {string} productId
     * @returns {Promise<boolean>} True if deleted successfully, false otherwise
     */
    async delete(productId) {
      const result = await this._withStore('readwrite', (store) =>
        store.delete(productId)
      );

      const success = result !== null;
      isDev() &&
        console.log(
          '[IdbCache]',
          success
            ? 'Deleted cache for product:'
            : 'Failed to delete cache for product:',
          productId
        );

      return success;
    }

    /**
     * Clears the entire PPD cache store.
     * @returns {Promise<boolean>} True if cleared successfully, false otherwise
     */
    async clearAll() {
      const result = await this._withStore('readwrite', (store) =>
        store.clear()
      );

      const success = result !== null;
      isDev() &&
        console.log(
          '[IdbCache]',
          success ? 'Cache cleared successfully' : 'Failed to clear cache'
        );

      return success;
    }

    /**
     * Quick existence check for a product ID without returning the full entry.
     * @param {string} productId
     * @returns {Promise<boolean>} True if entry exists, false otherwise
     */
    async has(productId) {
      const result = await this._withStore('readonly', (store) => {
        // Use count with a key to avoid fetching the full ~8MB object
        return store.count(productId);
      });

      return result > 0;
    }
  }

  // Register on the MurphyDoorConfigurator namespace
  window.MurphyDoorConfigurator.idbCache = new IdbCache();
})(window);
